import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PurchaseOrderService } from '../../purchase-order.service';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, catchError } from 'rxjs/operators';
import { FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'app-add-purchase-order-details',
    templateUrl: './add-purchase-order-details.component.html',
    styleUrls: ['./add-purchase-order-details.component.scss'],
    standalone: false
})
export class AddPurchaseOrderDetailsComponent implements OnInit {
  @Input() addPurchaseOrderObject: any;
  @Output() selectedTypeEvent: EventEmitter<any> = new EventEmitter();

  private readonly debounceTimeMs = 500;
  typeList = [
    { label: 'Regular', value: 'regular' },
    { label: 'Bulk', value: 'bulk' },
    { label: 'Vendor', value: 'vendor' }
  ];
  selectedType: string = 'bulk';

  jobInput$ = new Subject<string>();
  isJobLoading = false;
  jobList: any[] = [];

  vendorInput$ = new Subject<string>();
  isVendorLoading = false;
  vendorList: any[] = [];

  jobAdditionalParams: any;

  pageKey = 'pageSize';
  noOfRecPerPage = 10;
  searchKey = 'search';
  searchVal = '';
  currentPage = 1;
  lastPage = 1;
  sortBy = '';
  sortOrder = '';
  jobNameFormGroup!: FormGroup;

  constructor(public purchaseOrderService: PurchaseOrderService) {}

  ngOnInit(): void {
    this.formInit();
    this.setupJobSearch();
    this.loadDefaultJobs();
    this.setupVendorSearch();
    this.loadDefaultVendors();
  }

  formInit() {
    this.jobNameFormGroup = new FormGroup({
      job_name: new FormControl('', Validators.required)
    });
  }

  private buildSearchParams(term: string = ''): any {
    const page = this.currentPage < this.lastPage ? this.currentPage + 1 : this.lastPage;
    const data: any = {
      [this.pageKey]: this.noOfRecPerPage,
      [this.searchKey]: term,
      loadingService: false,
      page,
    };

    if (this.sortBy) data['sort_by'] = this.sortBy;
    if (this.sortOrder) data['sort_order'] = this.sortOrder;

    return data;
  }

  // 🟢 Merge selected items with result to preserve display
  private mergeSelectedWithResults(newList: any[]): any[] {
    const selected = Array.isArray(this.addPurchaseOrderObject.jobDetail)
      ? this.addPurchaseOrderObject.jobDetail
      : [this.addPurchaseOrderObject.jobDetail].filter(Boolean);

    const mergedList = [...selected, ...newList].filter(
      (item, index, self) => index === self.findIndex((t) => t?.id === item?.id)
    );

    return mergedList;
  }

  loadDefaultJobs() {
    this.isJobLoading = true;
    const params = this.buildSearchParams(this.searchVal);
    this.purchaseOrderService.search(params).pipe(
      catchError(() => of({ data: { data: [] } })),
      tap(() => (this.isJobLoading = false))
    ).subscribe((res: any) => {
      const newList = res?.data?.data || [];
      this.jobList = this.mergeSelectedWithResults(newList);
    });
  }

  setupJobSearch() {
    this.jobInput$.pipe(
      debounceTime(this.debounceTimeMs),
      distinctUntilChanged(),
      tap(() => this.isJobLoading = true),
      switchMap(term => {
        const searchTerm = term == null ? '' : term;
        return this.purchaseOrderService.search(this.buildSearchParams(searchTerm)).pipe(
          catchError(() => of({ data: { data: [] } })),
          tap(() => this.isJobLoading = false)
        );
      })
    ).subscribe((res: any) => {
      const newList = res?.data?.data || [];
      this.jobList = this.mergeSelectedWithResults(newList);
    });
  }

  selectedValueChange(event: any, type: string) {
    console.log('Selected Value Change:', event, type);

    if (type === 'bulk') {
      this.addPurchaseOrderObject.jobId = event?.map((x: any) => x.id);
      this.addPurchaseOrderObject.jobDetail = event;
    } else {
      this.addPurchaseOrderObject.jobId = event?.id;
      this.addPurchaseOrderObject.job_name = event?.name;
      this.addPurchaseOrderObject.jobDetail = event;
    }

    // 🟢 Reset the typeahead input value after selection
    this.jobInput$.next('');
  }

  selectedTypeChange() {
    // Clear job data
    this.addPurchaseOrderObject.jobDetail = null;
    this.addPurchaseOrderObject.jobId = null;
    this.addPurchaseOrderObject.jobAdditionalParams = null;

    // Clear vendor data
    this.addPurchaseOrderObject.vendorId = null;
    this.addPurchaseOrderObject.vendorName = null;
    this.addPurchaseOrderObject.vendorDetail = null;
  }

  onBulkJobsSelected(selectedIds: any[]) {
    const selectedObjects = this.jobList.filter(job => selectedIds.includes(job.id));
    this.addPurchaseOrderObject.jobDetail = selectedObjects;
  }

  removeSelected(item: any, clear: Function) {
    if (Array.isArray(this.addPurchaseOrderObject.jobId)) {
      this.addPurchaseOrderObject.jobId = this.addPurchaseOrderObject.jobId.filter((id: any) => id !== item.id);
      this.addPurchaseOrderObject.jobDetail = this.addPurchaseOrderObject.jobDetail.filter((j: any) => j.id !== item.id);
    } else {
      this.addPurchaseOrderObject.jobId = null;
      this.addPurchaseOrderObject.jobDetail = null;
    }

    if (clear) clear();
  }

  // ========== VENDOR SEARCH METHODS ==========

  loadDefaultVendors() {
    this.isVendorLoading = true;
    const params = {
      pageSize: this.noOfRecPerPage,
      search: '',
      page: 1
    };

    // Use general vendor search endpoint (not job-specific)
    this.purchaseOrderService.generalVendorSearch(params).pipe(
      catchError(() => of({ data: [] })),
      tap(() => (this.isVendorLoading = false))
    ).subscribe((res: any) => {
      this.vendorList = res?.data || [];
      console.log('Default vendors loaded:', this.vendorList);
    });
  }

  setupVendorSearch() {
    this.vendorInput$.pipe(
      debounceTime(this.debounceTimeMs),
      distinctUntilChanged(),
      tap(() => this.isVendorLoading = true),
      switchMap(term => {
        const searchTerm = term == null ? '' : term;
        const params = {
          pageSize: this.noOfRecPerPage,
          search: searchTerm,
          page: 1
        };
        // Use general vendor search endpoint (not job-specific)
        return this.purchaseOrderService.generalVendorSearch(params).pipe(
          catchError(() => of({ data: [] })),
          tap(() => this.isVendorLoading = false)
        );
      })
    ).subscribe((res: any) => {
      this.vendorList = res?.data || [];
      console.log('Vendor search results:', this.vendorList);
    });
  }

  onVendorSelected(event: any) {
    console.log('Vendor selected:', event);
    this.addPurchaseOrderObject.vendorId = event?.id;
    this.addPurchaseOrderObject.vendorName = event?.name;
    this.addPurchaseOrderObject.vendorDetail = event;

    console.log('Set vendorId:', this.addPurchaseOrderObject.vendorId);
    console.log('Set vendorName:', this.addPurchaseOrderObject.vendorName);

    // Reset vendor input after selection
    this.vendorInput$.next('');
  }

  removeVendorSelected(clear: Function) {
    this.addPurchaseOrderObject.vendorId = null;
    this.addPurchaseOrderObject.vendorName = null;
    this.addPurchaseOrderObject.vendorDetail = null;

    if (clear) clear();
  }
}
